/** XeAgents.ai**/
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, PencilLine } from "lucide-react";
import { RewriteButton } from "./RewriteButton";
import type { AIContentState } from "./types";

/**
 * A textarea the AI can fill, that the user owns the moment they touch it.
 *
 * The badge is doing real work, not decoration. Someone who cannot tell which
 * sentences a model wrote reviews none of them, and this copy is spoken to their
 * customers under their own company's name. "AI Generated" says check this;
 * "Edited by you" says this one is yours, and is what makes the rewrite dialog
 * name the overwrite before it happens.
 *
 * Kept deliberately quiet - a small outline badge, no coloured panel, no glow.
 * The content is the customer's pitch; the provenance is a footnote on it.
 */
export function AIContentField({
  id,
  label,
  labelClassName,
  required,
  hint,
  placeholder,
  state,
  error,
  rows = 4,
  onChange,
  onBlur,
  onRewrite,
  canRewrite,
}: {
  id: string;
  label: string;
  /** For labels taken from a variable key, which need capitalising. */
  labelClassName?: string;
  required?: boolean;
  hint: string;
  placeholder?: string;
  state: AIContentState;
  error?: string;
  rows?: number;
  onChange: (value: string) => void;
  onBlur?: () => void;
  onRewrite: (instruction: string) => void;
  /** False until there is something to write from - a website read, or details typed in. */
  canRewrite: boolean;
}) {
  const generating = state.origin === 'generating';

  return (
    <div className="space-y-1.5" data-testid={`field-${id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label
          htmlFor={id}
          className={`flex items-center gap-1.5 text-sm font-medium text-foreground ${labelClassName ?? ''}`}
        >
          {label}
          {required && <span className="text-destructive" aria-hidden="true">*</span>}
          {required && <span className="sr-only">(required)</span>}
        </label>

        <div className="flex items-center gap-1">
          {state.origin === 'ai' && (
            <Badge
              variant="outline"
              className="h-6 gap-1 border-primary/25 bg-primary/5 px-2 text-[11px] font-normal text-primary"
              data-testid={`badge-ai-${id}`}
            >
              <Sparkles className="h-3 w-3" />
              AI Generated
            </Badge>
          )}
          {state.origin === 'edited' && (
            <Badge
              variant="outline"
              className="h-6 gap-1 px-2 text-[11px] font-normal text-muted-foreground"
              data-testid={`badge-edited-${id}`}
            >
              <PencilLine className="h-3 w-3" />
              Edited by you
            </Badge>
          )}
          <RewriteButton
            fieldLabel={label}
            currentText={state.value}
            isEdited={state.origin === 'edited'}
            busy={generating}
            disabled={!canRewrite}
            onRewrite={onRewrite}
          />
        </div>
      </div>

      {generating ? (
        // Shaped like the text it is replacing, so the field does not collapse
        // and shove everything below it up the page while it writes.
        <div className="space-y-2 rounded-md border bg-muted/30 p-3" data-testid={`skeleton-${id}`}>
          <Skeleton className="h-3.5 w-[92%]" />
          <Skeleton className="h-3.5 w-[78%]" />
          <Skeleton className="h-3.5 w-[85%]" />
        </div>
      ) : (
        <Textarea
          id={id}
          rows={rows}
          value={state.value}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
          aria-invalid={!!error}
          aria-describedby={`${id}-hint`}
          className="resize-y text-sm leading-relaxed"
        />
      )}

      {error ? (
        <p className="text-xs font-medium text-destructive" role="alert" data-testid={`error-${id}`}>
          {error}
        </p>
      ) : (
        <p id={`${id}-hint`} className="text-xs text-muted-foreground leading-snug">
          {hint}
        </p>
      )}
    </div>
  );
}
